import { useQueryErrorResetBoundary } from '@tanstack/react-query';
import type { ErrorComponentProps } from '@tanstack/react-router';
import { useNavigate, useRouter } from '@tanstack/react-router';
import { useEffect, useState } from 'react';

import { Error, ErrorFooter, ErrorMessage, ErrorTitle } from './Error';
import type { GeneralErrorBoundaryProps } from './GeneralErrorBoundary';
import { GeneralErrorBoundary } from './GeneralErrorBoundary';
import { Button, Flex } from '@radix-ui/themes';
import { getErrorMessage, HttpError } from '~/utils/errors';
import { ErrorPage } from '~/icons/ErrorPage';
import { Header } from '../Header/Header';
import { AccountDropdown } from '~/components/AccountDropdown/AccountDropdown';
import { useSession } from '~/features/auth/context/Session';
type RouteErrorBoundaryProps = GeneralErrorBoundaryProps & { reset: ErrorComponentProps['reset'] };

function Default({ error }: { error: string }) {
  const navigate = useNavigate();

  return (
    <Error>
      <ErrorTitle>Something went wrong...</ErrorTitle>
      <ErrorMessage>Sorry about that, refresh your page or go back home to try again.</ErrorMessage>
      <ErrorFooter>
        <Button
          variant="outline"
          color="gray"
          onClick={() =>
            void navigate({
              to: '/',
            })
          }
        >
          Go back
        </Button>
      </ErrorFooter>
      <Flex align="center" justify="center" width="100%">
        <ErrorPage />
      </Flex>
    </Error>
  );
}

function DefaultStatus({ handleRetry, error }: { handleRetry: () => void; error: HttpError }) {
  const navigate = useNavigate();

  return (
    <Error>
      <ErrorTitle>Something went wrong...</ErrorTitle>
      <ErrorMessage>Sorry about that, refresh your page or go back home to try again.</ErrorMessage>
      <ErrorFooter>
        <Button
          variant="outline"
          color="gray"
          onClick={() =>
            void navigate({
              to: '/',
            })
          }
        >
          Go back
        </Button>
      </ErrorFooter>
      <Flex align="center" justify="center" width="100%">
        <ErrorPage />
      </Flex>
    </Error>
  );
}

export function RouteErrorBoundary({ reset, ...restProps }: RouteErrorBoundaryProps) {
  const router = useRouter();
  const { session } = useSession();
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);

  const queryErrorResetBoundary = useQueryErrorResetBoundary();

  useEffect(() => {
    queryErrorResetBoundary.reset();
  }, [queryErrorResetBoundary]);

  function handleRetry() {
    reset();
    void router.invalidate();
  }

  return (
    <Flex direction="column" width="100%" height="100%" minHeight="100vh">
      <Header>
        {session && <AccountDropdown isPopoverOpen={isPopoverOpen} setIsPopoverOpen={setIsPopoverOpen} />}
      </Header>
      <Flex align="center" justify="center" width="100%" flexGrow="1">
        <GeneralErrorBoundary
          {...restProps}
          defaultStatusHandler={(error) => <DefaultStatus error={error.error} handleRetry={() => handleRetry()} />}
          unexpectedErrorHandler={(error) => <Default error={getErrorMessage(error)} />}
        />
      </Flex>
    </Flex>
  );
}
